import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import type { AudienceResponse } from '@troia-v3/types';
import { audienceStatusMeta, audienceTypeMeta, formatEntries } from './audiencePresentation';

/**
 * Card de público (view "Cards"). Padrão-lista F34: cards SEM checkbox —
 * seleção em massa vive só na view de lista/tabela. Menu ••• de ações
 * (Ver contatos / Excluir). Ícone-categoria (mail/phone) com tint primário.
 */
export interface AudienceCardProps {
  audience: AudienceResponse;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const AudienceCard: React.FC<AudienceCardProps> = ({
  audience,
  onView,
  onDelete,
}) => {
  const type = audienceTypeMeta[audience.identifierType];
  const status = audienceStatusMeta[audience.processingStatus] ?? audienceStatusMeta.idle;

  const actions: CardAction[] = [];
  if (onView) actions.push({ label: 'Ver contatos', icon: 'user', onClick: () => onView(audience.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(audience.id), variant: 'danger' });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors flex flex-col"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`audience-card-${audience.id}`}
    >
      {/* Cabeçalho: ícone-categoria + nome + menu */}
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
        >
          <Icon name={type.icon} className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <Link
            to={`/prospecting/audiences/${audience.id}`}
            className="font-bold text-sm truncate block hover:underline"
            style={{ color: 'var(--theme-card-text)' }}
            data-testid={`audience-name-${audience.id}`}
          >
            {audience.name}
          </Link>
          <p
            className="text-xs mt-0.5"
            style={{
              color: 'var(--theme-muted-text)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {audience.description || 'Sem descrição'}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`audience-actions-${audience.id}`} />
          </div>
        )}
      </div>

      {/* Badges: tipo + status */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={type.icon} className="w-3 h-3" />
          {type.label}
        </span>
        <StatusPill
          variant={status.variant}
          label={status.label}
          data-testid={`audience-status-${audience.id}`}
        />
      </div>

      {/* Rodapé: entradas + data */}
      <div
        className="mt-3 pt-3 flex items-center gap-2 text-xs"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        <Icon name="users" className="w-3.5 h-3.5" />
        <span className="tabular-nums" style={{ color: 'var(--theme-card-text)', fontWeight: 700 }}>
          {formatEntries(audience.totalEntries)}
        </span>
        <span>entradas</span>
        <span className="ml-auto">{new Date(audience.createdAt).toLocaleDateString('pt-BR')}</span>
      </div>
    </div>
  );
};

AudienceCard.displayName = 'AudienceCard';
